import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card'
import { Button } from '../../ui/button'
import { Input } from '../../ui/input'
import { Label } from '../../ui/label'
import { ArrowLeft, ArrowRight, AlertCircle } from 'lucide-react'
import type { DealFormData } from '../../../lib/types'

interface StepTimelinePlanProps {
  formData: DealFormData
  onComplete: (data: Partial<DealFormData>) => void
  onUpdate: (data: Partial<DealFormData>) => void
  onBack: () => void
  headingId?: string
  onValidationError?: (errorMessage: string) => void
  onValidationErrors?: (errors: { id: string; label: string }[]) => void
  requestChanges?: Record<string, string>
}

export function StepTimelinePlan({
  formData,
  onComplete,
  onUpdate,
  onBack,
  headingId,
  onValidationError,
  onValidationErrors,
  requestChanges,
}: StepTimelinePlanProps) {
  const [localData, setLocalData] = useState({
    purchaseStartDate: formData.purchaseStartDate || '',
    purchaseEndDate: formData.purchaseEndDate || '',
    mintStartDate: formData.mintStartDate || '',
    tgeTargetDate: formData.tgeTargetDate || '',
  })
  const [dateErrors, setDateErrors] = useState<Record<string, string>>({})

  const getTodayDateTimeString = (): string => {
    return new Date().toISOString().slice(0, 16)
  }

  const ensureUTCDefault = (
    fieldName: keyof typeof localData,
    options?: { optional?: boolean },
  ) => {
    if (options?.optional) return
    if (!(localData as any)[fieldName]) {
      updateField(fieldName as string, getTodayDateTimeString())
    }
  }

  const validateDate = (date: string, fieldName: string): string => {
    if (!date) return `${fieldName} is required.`
    const selectedDate = new Date(date + 'Z')
    const today = new Date()
    if (selectedDate < today) return `${fieldName} cannot be in the past.`
    return ''
  }

  const validateDateRange = (startDate: string, endDate: string): string => {
    if (!startDate || !endDate) return ''
    const start = new Date(startDate + 'Z')
    const end = new Date(endDate + 'Z')
    if (end <= start) return 'End date must be after start date.'
    return ''
  }

  const validateAllDates = (data: typeof localData): Record<string, string> => {
    const errors: Record<string, string> = {}
    const isOtcDeal = formData.salesType === 'OTC Deal'

    const startDateError = validateDate(data.purchaseStartDate, 'Start date')
    if (startDateError) errors.purchaseStartDate = startDateError

    if (!isOtcDeal) {
      const endDateError = validateDate(data.purchaseEndDate, 'End date')
      const mintError = validateDate(data.mintStartDate, 'Mint start time')
      const tgeError = data.tgeTargetDate
        ? validateDate(data.tgeTargetDate, 'TGE date')
        : ''

      if (endDateError) errors.purchaseEndDate = endDateError
      if (mintError) errors.mintStartDate = mintError
      if (tgeError) errors.tgeTargetDate = tgeError

      const rangeError = validateDateRange(
        data.purchaseStartDate,
        data.purchaseEndDate,
      )
      if (rangeError && !errors.purchaseEndDate)
        errors.purchaseEndDate = rangeError

      if (data.mintStartDate) {
        if (!data.purchaseEndDate) {
          errors.mintStartDate =
            'Set the Purchase Window end date before the mint start time.'
        } else {
          const mintStart = new Date(data.mintStartDate + 'Z')
          const depositEnd = new Date(data.purchaseEndDate + 'Z')
          if (mintStart <= depositEnd) {
            errors.mintStartDate =
              'Mint start time must be after the Purchase Window ends.'
          }
        }
      }
    }

    return errors
  }

  const updateField = (field: string, value: string) => {
    const updatedData = { ...localData, [field]: value }
    setLocalData(updatedData)
    const errors = validateAllDates(updatedData)
    setDateErrors(errors)
    onUpdate(updatedData)
  }

  const handleSubmit = () => {
    const errors = validateAllDates(localData)
    setDateErrors(errors)
    const errorIds = Object.keys(errors).filter(
      (key) => !!errors[key as keyof typeof errors],
    )
    if (errorIds.length === 0) {
      onValidationErrors?.([])
      onValidationError?.('')
      onComplete(localData)
    } else {
      const fieldLabels: Record<string, string> = {
        purchaseStartDate: 'Purchase Start Date',
        purchaseEndDate: 'Purchase End Date',
        mintStartDate: 'Mint Start Date',
        tgeTargetDate: 'TGE Target Date',
      }

      const mappedErrors = errorIds.map((id) => ({
        id,
        label: fieldLabels[id] || id,
      }))

      if (onValidationErrors) {
        onValidationErrors(mappedErrors)
      } else {
        onValidationError?.(
          'Please complete all required fields before continuing.',
        )
      }
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Purchase Window</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="purchaseStartDate">Start Date (UTC)</Label>
              <Input
                id="purchaseStartDate"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:MM"
                value={localData.purchaseStartDate}
                onChange={(e) =>
                  updateField('purchaseStartDate', e.target.value)
                }
                onFocus={() => ensureUTCDefault('purchaseStartDate')}
                className={`placeholder:text-neutral-400 ${
                  dateErrors.purchaseStartDate ? 'border-red-500 bg-red-50' : ''
                }`}
              />
              <p className="text-xs text-neutral-400">Deposit opens.</p>
              <div className="flex flex-col gap-1">
                {dateErrors.purchaseStartDate && (
                  <p className="text-xs text-status-error inline-flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {dateErrors.purchaseStartDate}
                  </p>
                )}
                {requestChanges?.purchaseStartDate && (
                  <p className="text-status-error text-xs inline-flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {requestChanges.purchaseStartDate}
                  </p>
                )}
              </div>
            </div>
            {formData.salesType !== 'OTC Deal' && (
              <div className="space-y-2">
                <Label htmlFor="purchaseEndDate">End Date (UTC)</Label>
                <Input
                  id="purchaseEndDate"
                  type="datetime-local"
                  placeholder="YYYY-MM-DD HH:MM"
                  value={localData.purchaseEndDate}
                  onChange={(e) =>
                    updateField('purchaseEndDate', e.target.value)
                  }
                  onFocus={() => ensureUTCDefault('purchaseEndDate')}
                  className={`placeholder:text-neutral-400 ${
                    dateErrors.purchaseEndDate
                      ? 'border border-status-error bg-red-50 focus:border-status-error'
                      : ''
                  }`}
                />
                <p className="text-xs text-neutral-400">Deposit closes.</p>
                <div className="flex flex-col gap-1">
                  {dateErrors.purchaseEndDate && (
                    <p className="text-xs text-status-error inline-flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {dateErrors.purchaseEndDate}
                    </p>
                  )}
                  {requestChanges?.purchaseEndDate && (
                    <p className="text-status-error text-xs inline-flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {requestChanges.purchaseEndDate}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {formData.salesType !== 'OTC Deal' && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Mint Start Time</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="mintStartDate">Start Time (UTC)</Label>
                <Input
                  id="mintStartDate"
                  type="datetime-local"
                  placeholder="dd/mm/yyyy, --:-- --"
                  value={localData.mintStartDate}
                  onChange={(e) => updateField('mintStartDate', e.target.value)}
                  onFocus={() => ensureUTCDefault('mintStartDate')}
                  className={`placeholder:text-neutral-400 ${
                    dateErrors.mintStartDate
                      ? 'border border-status-error bg-red-50 focus:border-status-error'
                      : ''
                  }`}
                />
                <p className="text-xs text-neutral-400">
                  Time when voucher NFTs will be minted and made available to
                  users. Must be after the Purchase Window ends.
                </p>
                <div className="flex flex-col gap-1">
                  {dateErrors.mintStartDate && (
                    <p className="text-xs text-status-error inline-flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {dateErrors.mintStartDate}
                    </p>
                  )}
                  {requestChanges?.mintStartDate && (
                    <p className="text-status-error text-xs inline-flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {requestChanges.mintStartDate}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                Token Generation Event (optional)
              </CardTitle>
              <p className="text-sm text-neutral-600">
                The token's vesting and unlocks begin at the TGE.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="tgeTargetDate">Target Date (UTC)</Label>
                <Input
                  id="tgeTargetDate"
                  type="datetime-local"
                  placeholder="YYYY-MM-DD HH:MM"
                  value={localData.tgeTargetDate}
                  onChange={(e) => updateField('tgeTargetDate', e.target.value)}
                  onFocus={() => ensureUTCDefault('tgeTargetDate')}
                  className={`placeholder:text-neutral-400 ${
                    dateErrors.tgeTargetDate
                      ? 'border border-status-error bg-red-50 focus:border-status-error'
                      : ''
                  }`}
                />
                <p className="text-xs text-neutral-400">
                  Tentative date when tokens are deposited into the distribution
                  contract. Please align with the Project team to confirm.{' '}
                  <span className="font-medium"></span>
                </p>
                <div className="mt-3 flex items-start gap-2 rounded-sm border border-yellow-500 bg-yellow-50 p-2 text-xs text-yellow-500">
                  <p>
                    You may continue without a TGE date. This can be updated
                    later when information is available.
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  {dateErrors.tgeTargetDate && (
                    <p className="text-xs text-status-error inline-flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {dateErrors.tgeTargetDate}
                    </p>
                  )}
                  {requestChanges?.tgeTargetDate && (
                    <p className="text-status-error text-xs inline-flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {requestChanges.tgeTargetDate}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      <div className="flex justify-between pt-6">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          size="md"
          className="flex items-center gap-2 bg-transparent px-5"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
        <Button
          onClick={handleSubmit}
          variant="brand"
          size="md"
          className="flex items-center gap-2 px-5"
        >
          Continue
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  )
}
